import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
  Warning,
} from '../../components/DocsComponents'

const PlanningDocuments = () => (
  <Page
    title="The Planning Workspace"
    intro="The Planning workspace panel on the event record works through the plan in focused stages: Planning Intelligence, Operational plan, Clinical & EMP, Risk assessment, CQC Exposure, Post-event, and Costs. This page covers the three core planning tabs and Planning Intelligence."
  >
    <Section title="Operational plan">
      <Bullets>
        <Bullet>
          A <strong>Vehicles</strong> card books vehicles onto the event, and
          the form below covers the operational picture: Personnel Summary,
          Resources Summary, Address/Location, Event Organiser Contact,
          What3Words, Description, Vehicle Movements, Site Build, Staff
          Parking, and Staff Welfare. The venue's structured address and map
          point live on the event details themselves (edit the event to change
          them) — the map point centres the event's control room map.
        </Bullet>
        <Bullet>
          Key people are picked from your staff list: Onsite Team Leader, Ops
          Manager, and Clinical Lead.
        </Bullet>
      </Bullets>
      <Info title="Reusable wording">
        Many planning fields show an orange suggestions icon and, while empty,
        chips of wording drawn from your saved EMP templates — click one to
        reuse it, or open the icon to browse and append. See the Event
        Medical Plan page for how templates are saved.
      </Info>
    </Section>

    <Section title="Clinical &amp; EMP">
      <Bullets>
        <Bullet>
          <strong>Command, Communications &amp; Clinical Governance</strong> —
          command and comms, patient care records, public health,
          safeguarding, IPC considerations, and special clinical notes.
        </Bullet>
        <Bullet>
          <strong>Access, Transport &amp; Receiving Hospitals</strong> —
          emergency vehicle access, the hospital transport plan, and pickers
          for the nearest Emergency Department, Trauma Unit, Major Trauma
          Centre, PPCI, Stroke Centre, Minor Injury Unit, Paediatric Hospital,
          and Paediatric MTC.
        </Bullet>
        <Bullet>
          <strong>Public Protection &amp; Martyn's Law</strong> — set the
          Martyn's Law tier (standard, or enhanced for qualifying events of
          800+); the full set of evacuation, invacuation, lockdown, alerting,
          and security fields appears once a tier is selected.
        </Bullet>
        <Bullet>
          <strong>Escalation, Overwhelm &amp; Major Incidents</strong> —
          escalation of care, the major incident plan, the operational
          overwhelm plan, and the event's{' '}
          <strong>Operational Pressures Escalation Levels (OPELs)</strong>.
          Start from <strong>Add Standard OPELs</strong> for the usual OPEL
          1–4 structure, then tailor each level's indicators and actions.
        </Bullet>
      </Bullets>
      <Info title="Managing hospital destinations">
        Destination pickers draw on your organisation's shared hospitals
        list. Add or edit a destination inline from the picker (name, types,
        public and confidential phone numbers, address, What3Words, access
        notes), or manage the whole list under Site Configuration &gt; Events
        &gt; Medical Plans &gt; Edit Hospitals. Confidential numbers are
        marked as such on the printed EMP.
      </Info>
    </Section>

    <Section title="Risk assessment">
      <Bullets>
        <Bullet>
          A structured questionnaire rather than a free-form register: high
          risk activities (with your own tags), venue type, expected
          attendance, alcohol and recreational drug use, risk of injury,
          likelihood of hospital referrals, overnight camping, and data from
          previous events.
        </Bullet>
        <Bullet>
          Tagging <strong>Equestrian</strong> or{' '}
          <strong>Motor Racing or Motorsports</strong> reveals extra
          discipline-specific questions (affiliation and fence heights, or
          track control, fire marshalls, extrication, and driver comms).
        </Bullet>
        <Bullet>
          <strong>Risks &amp; Mitigations</strong> covers workforce,
          communications failure, major incidents, terrorist attack, vehicle
          failure, and the guidelines used (for example the Purple Guide).
        </Bullet>
        <Bullet>
          <strong>Method Statements</strong> captures your standard wording
          for preparation, staffing and training, facilities, medications,
          communications, NHS impact, partnership working, escalating
          concerns, confidentiality, post event analysis, and provider
          feedback.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Planning Intelligence">
      <Bullets>
        <Bullet>
          The first tab gives live planning support from the event details,
          previous data, and known risk factors: a{' '}
          <strong>Confidence</strong> score, a clinical risk rating, expected
          presentations and hospital transfers with planning ranges, the
          presentation rate per 1,000 attendees, and expected peak demand.
        </Bullet>
        <Bullet>
          <strong>Risk drivers</strong> explains what's pushing the estimates
          up, and <strong>Planning gaps &amp; next actions</strong> lists
          missing information ranked Critical / Warning / Info, each with a
          button that jumps to the right tab.
        </Bullet>
        <Bullet>
          Use <strong>Recalculate</strong> after significant changes — the
          figures are estimates and should always be reviewed by a competent
          event medical planner.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Progress and saving">
      <Bullets>
        <Bullet>
          The <strong>Planning Progress</strong> bars on the Event Medical
          Plan card show indicative completion of the Ops Plan, Risk
          Assessment, and Clinical Plan while the EMP is still in draft.
        </Bullet>
      </Bullets>
      <Warning title="Save before you leave">
        All three planning tabs share the event record's single{' '}
        <strong>Save Changes</strong> bar — nothing in them is saved until
        you use it.
      </Warning>
      <Info title="Who can do this?">
        Editing any planning tab needs the <strong>Event Planning</strong>{' '}
        team (site administrators always have access). The CQC Exposure tab
        accepts input from Event Planning or Governance.
      </Info>
    </Section>
  </Page>
)

export default PlanningDocuments
